'use client';

import { useContext, useEffect, useRef, useState } from "react";
import TextInput from "../common/inputs/text_input";
import classes from "./public_provider_form.module.css";
import Send from '@/assets/svg/icons/send.svg';
import MessageCircle from '@/assets/svg/icons/message-circle.svg';
import CustomButton from "../common/buttons/button";
import CustomDatepicker from "../common/datepicker/datepicker";
import FormControl from "../common/checks_and_radios/form_control";
import Link from "next/link";
import dynamic from "next/dynamic";
import ToastContext from "@/providers/toast-context";
import { sendNewOffer, sendNewOfferPublic } from "@/utils/http";
import { useMutation } from "@tanstack/react-query";
import X from '@/assets/svg/icons/x.svg';
import { Offcanvas } from "react-bootstrap";
import AuthContext from "@/providers/auth-context";
import { useSession } from "next-auth/react";
import CustomModal from "../common/modal/modal";
import { createDateTimeString } from "@/functions/create_date_time_string";
import { formatDate } from "@/functions/form_date";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { getRecaptchaToken, needsEmailVerification, isRecaptchaError, getRecaptchaErrorMessage, getRecaptchaScore } from "@/utils/recaptcha";
import { useSanitizedHTMLStrict } from "@/utils/sanitizeHTMLHooks";
import { usePublicProviderInteraction } from "./public_provider_interaction_context";
import useAnalyticsTracking from "../common/analytics/use_analytics_tracking";
import useOpenAiAdsPixel from "../common/analytics/use_openai_ads_pixel";
import Avatar from "../common/avatar/avatar";

const ProviderDropdownPublic = dynamic(() => import("../common/provider_selector_simple/provider_selector_public"));
const EmailVerificationModal = dynamic(() => import("../common/email_verification_modal/email_verification_modal"), { ssr: false });
const RecaptchaV2Modal = dynamic(() => import("../common/recaptcha_v2_modal/recaptcha_v2_modal"), { ssr: false });

export default function PublicProviderForm({ provider, contactMobile: contactMobileProp, setContactMobile: setContactMobileProp }) {
    const interaction = usePublicProviderInteraction();
    const contactMobile = contactMobileProp ?? interaction.contactMobile;
    const setContactMobile = setContactMobileProp || interaction.setContactMobile;
    const formRef = useRef(null);
    const inlineFormRef = useRef(null);
    const inlineViewTrackedRef = useRef(false);
    const submitLockRef = useRef(false);
    const { trackEvent } = useAnalyticsTracking('googleTagManager');
    const { measure: measureOpenAiAds } = useOpenAiAdsPixel();

    useEffect(() => {
        if (interaction.formFocusTrigger > 0 && formRef.current) {
            const firstInput = formRef.current.querySelector('input, textarea');
            if (firstInput) firstInput.focus();
        }
    }, [interaction.formFocusTrigger]);
    const [isMobileViewport, setIsMobileViewport] = useState(false);
    const [name, setName] = useState("");
    const [nameIsError, setNameIsError] = useState(false);
    const [email, setEmail] = useState("");
    const [city, setCity] = useState("");
    const [providerId, setProviderId] = useState(0);
    const [providerName, setProviderName] = useState("");
    const [emailIsError, setEmailIsError] = useState("");
    const [phone, setPhone] = useState("");
    const [phoneError, setPhoneError] = useState(false);
    const [note, setNote] = useState("");
    const [noteError, setNoteError] = useState(false);
    const [date, setDate] = useState(null);
    const [dateError, setDateError] = useState(false);
    const [estimatedParticipants, setEstimatedParticipants] = useState(100);
    const [estimatedParticipantsError, setEstimatedParticipantsError] = useState("");
    const [estimatedParticipantsWarning, setEstimatedParticipantsWarning] = useState("");
    const [aszf, setAszf] = useState(false);
    const [aszfError, setAszfError] = useState(false);
    const [showVerificationModal, setShowVerificationModal] = useState(false);
    const [verificationScore, setVerificationScore] = useState(null);
    const [showRecaptchaV2Modal, setShowRecaptchaV2Modal] = useState(false);
    const [pendingPayload, setPendingPayload] = useState(null);
    const [isPreparingSubmit, setIsPreparingSubmit] = useState(false);
    const toastCtx = useContext(ToastContext);
    const { status } = useSession();
    const [successModal, setSuccessModal] = useState(false);
    const { setShowLogin, setShowLoginState, setRedirectURL, userData, weddingId } = useContext(AuthContext);
    const [lastMessage, setLastMessage] = useState({});
    const { executeRecaptcha } = useGoogleReCaptcha();
    const PROV_TYPE__HELYSZIN = "1";

    useEffect(() => {
        const updateViewport = () => {
            const isMobile = window.innerWidth <= 902;

            setIsMobileViewport(isMobile);

            if (!isMobile && contactMobile) {
                setContactMobile(false);
            }
        };

        updateViewport();
        window.addEventListener("resize", updateViewport);

        return () => window.removeEventListener("resize", updateViewport);
    }, [contactMobile, setContactMobile]);

    useEffect(() => {
        if (!isMobileViewport || contactMobile || !inlineFormRef.current || inlineViewTrackedRef.current) return;

        const observer = new IntersectionObserver(([entry]) => {
            if (!entry.isIntersecting || inlineViewTrackedRef.current) return;

            inlineViewTrackedRef.current = true;
            trackEvent('offer_request_view', {
                provider_id: provider.id,
                provider_name: provider.name,
                source: 'inline_bottom'
            });
            observer.disconnect();
        }, { threshold: 0.25 });

        observer.observe(inlineFormRef.current);
        return () => observer.disconnect();
    }, [contactMobile, isMobileViewport, provider.id, provider.name, trackEvent]);
    
    useEffect(() => {
        const last_messages = localStorage.getItem("last_messages");
        const storedLastMessages = last_messages ? JSON.parse(last_messages) : {};
        const storedLastMessage = storedLastMessages[provider.id];

        if (provider && provider.last_message && provider.last_message.message) setLastMessage(provider.last_message);
        else if (storedLastMessage?.message) {
            setLastMessage({ message: storedLastMessage.message, ts: storedLastMessage.sent_at || null });
        }

        let is_other = (provider.type?.id && provider.type.id.toString() !== PROV_TYPE__HELYSZIN);
        const last_offer = localStorage.getItem(is_other ? "last_offer_other" : "last_offer");
        if (last_offer) {
            const parsed_last_offer = JSON.parse(last_offer);
            if (parsed_last_offer["name"]) setName(parsed_last_offer["name"]);
            if (parsed_last_offer["email"]) setEmail(parsed_last_offer["email"]);
            if (parsed_last_offer["phone"]) setPhone(parsed_last_offer["phone"]);
            if (parsed_last_offer["date"]) setDate(new Date(parsed_last_offer["date"]));
            if (parsed_last_offer["max_participants"]) setEstimatedParticipants(parsed_last_offer["max_participants"]);
            if (parsed_last_offer["message"]) setNote(parsed_last_offer["message"]);
            if (is_other) {
                if (parsed_last_offer["city"]) setCity(parsed_last_offer["city"]);
                if (parsed_last_offer["wedding_provider_id"]) setProviderId(parsed_last_offer["wedding_provider_id"]);
                if (parsed_last_offer["wedding_provider_name"]) setProviderName(parsed_last_offer["wedding_provider_name"]);
            }
        }
    }, []);

    const todoAfterSuccess = (response, params) => {
        if (response.success) {
            // Ellenőrizzük, hogy kell-e email verification
            if (needsEmailVerification(response)) {
                const score = getRecaptchaScore(response);
                setVerificationScore(score);
                setShowVerificationModal(true);
                setContactMobile(false);
                return; // Ne folytassuk a sikeres flow-t
            }

            if (provider.type?.id && provider.type.id.toString() !== PROV_TYPE__HELYSZIN) localStorage.setItem("last_offer_other", JSON.stringify(params.params));
            else localStorage.setItem("last_offer", JSON.stringify(params.params));

            setName("");
            setEmail("");
            setPhone("");
            setNote("");
            setCity("");
            setProviderId(0);
            setProviderName("");
            setDate(new Date());
            setEstimatedParticipants(100);
            setAszf(false);

            const sentAt = response.response?.sent_at || new Date().toISOString();
            const latestMessage = { message: params.params.message, ts: sentAt };
            setLastMessage(latestMessage);

            if (status === "authenticated" && userData.id !== 0) toastCtx.addToast({ type: "success", header_text: "Sikeres küldés", sub_text: `Sikeresen elküldted az üzenetet!` });
            else {
                const temp_messages = localStorage.getItem("last_messages");
                const storedMessage = { message: params.params.message, sent_at: sentAt };
                if (temp_messages) localStorage.setItem("last_messages", JSON.stringify({ ...JSON.parse(temp_messages), [provider.id]: storedMessage }));
                else localStorage.setItem("last_messages", JSON.stringify({ [provider.id]: storedMessage }));

                setSuccessModal(true);
            }

            if (typeof window !== "undefined" && window.dataLayer) {
                // A backendtől kapott válasz alapján döntjük el, hogy első vagy visszatérő üzenet
                const contactType = (response.response && response.response.is_first_message) ? "first" : "returning";

                window.dataLayer.push({
                    event: 'offer_request_success',
                    provider_id: provider.id,
                    provider_name: provider.name,
                    contact_type: contactType
                });
            }

            measureOpenAiAds('lead_created');

            setContactMobile(false);
        }
        else {
            toastCtx.addToast({ type: "error", header_text: "Üzenet küldése sikertelen", sub_text: response.message });
        }
    }

    const releaseSubmitLock = () => {
        submitLockRef.current = false;
        setIsPreparingSubmit(false);
    };

    // reCAPTCHA v2 modal handlers
    const handleV2Success = async (token) => {
        if (!pendingPayload) return;

        try {
            // Always retry with a fresh v3 token (backend expects it in captcha_response)
            if (!executeRecaptcha) {
                console.error("not available to execute recaptcha");
                releaseSubmitLock();
                return;
            }
            const v3Token = await getRecaptchaToken(executeRecaptcha, "offer");

            // pendingPayload.params can be string (axios config.data) or object (we store it)
            const payloadData = typeof pendingPayload.params === "string"
                ? JSON.parse(pendingPayload.params)
                : { ...pendingPayload.params };

            payloadData.captcha_response = v3Token;
            payloadData.recaptcha_v2_response = token;
            payloadData.recaptcha_v2_validated = true;

            setShowRecaptchaV2Modal(false);

            if (pendingPayload.wedding_id) {
                sendAuthenticatedForm({
                    wedding_id: pendingPayload.wedding_id,
                    params: payloadData,
                    provider_id: pendingPayload.provider_id
                });
            } else {
                sendForm({
                    params: payloadData,
                    provider_id: pendingPayload.provider_id
                });
            }
        } finally {
            setPendingPayload(null);
        }
    };

    const handleV2Close = () => {
        setShowRecaptchaV2Modal(false);
        setPendingPayload(null);
        releaseSubmitLock();
        // Note field and other form data remain intact
    };

    const { mutate: sendForm, isPending: isSendingPublicForm } = useMutation({
        mutationFn: sendNewOfferPublic,
        onSuccess: (response, params) => {
            // Backend sometimes returns HTTP 200 with success:false + recaptcha_v2_required
            if (response?.success === false && response?.recaptcha_v2_required) {
                setPendingPayload({ params: params?.params, provider_id: provider.id });
                setShowRecaptchaV2Modal(true);
                releaseSubmitLock();
                return;
            }

            todoAfterSuccess(response, params);
            releaseSubmitLock();
        },
        onError: (error) => {
            console.error("Send form error:", error);
            
            // reCAPTCHA v2 fallback check
            if (error?.response?.data?.recaptcha_v2_required) {
                setPendingPayload({ params: error.config.data, provider_id: provider.id });
                setShowRecaptchaV2Modal(true);
                releaseSubmitLock();
                return;
            }
            
            if (isRecaptchaError(error)) {
                toastCtx.addToast({ 
                    type: "error", 
                    header_text: "Biztonsági ellenőrzés", 
                    sub_text: getRecaptchaErrorMessage(error) 
                });
            } else {
                toastCtx.addToast({ 
                    type: "error", 
                    header_text: "Üzenet küldése sikertelen", 
                    sub_text: "Kérjük, próbáld újra később!" 
                });
            }

            releaseSubmitLock();
        }
    });

    const { mutate: sendAuthenticatedForm, isPending: isSendingAuthenticatedForm } = useMutation({
        mutationFn: sendNewOffer,
        onSuccess: (response, params) => {
            // Backend sometimes returns HTTP 200 with success:false + recaptcha_v2_required
            if (response?.success === false && response?.recaptcha_v2_required) {
                setPendingPayload({ wedding_id: weddingId, params: params?.params, provider_id: provider.id });
                setShowRecaptchaV2Modal(true);
                releaseSubmitLock();
                return;
            }

            todoAfterSuccess(response, params);
            releaseSubmitLock();
        },
        onError: (error) => {
            console.error("Send authenticated form error:", error);
            
            // reCAPTCHA v2 fallback check
            if (error?.response?.data?.recaptcha_v2_required) {
                setPendingPayload({ wedding_id: weddingId, params: error.config.data, provider_id: provider.id });
                setShowRecaptchaV2Modal(true);
                releaseSubmitLock();
                return;
            }
            
            if (isRecaptchaError(error)) {
                toastCtx.addToast({ 
                    type: "error", 
                    header_text: "Biztonsági ellenőrzés", 
                    sub_text: getRecaptchaErrorMessage(error) 
                });
            } else {
                toastCtx.addToast({ 
                    type: "error", 
                    header_text: "Üzenet küldése sikertelen", 
                    sub_text: "Kérjük, próbáld újra később!" 
                });
            }

            releaseSubmitLock();
        }
    });

    const nameInputChange = (val) => {
        setName(val);
        setNameIsError(false);
    }

    const emailInputChange = (val) => {
        setEmail(val);
        setEmailIsError("");
    }

    const EMAIL_TYPO_MAP = {
        'gamil.com': 'gmail.com', 'gnail.com': 'gmail.com', 'gmail.hu': 'gmail.com',
        'gmai.com': 'gmail.com', 'gmial.com': 'gmail.com', 'gmaill.com': 'gmail.com',
    };
    const EMAIL_KNOWN_DOMAINS = [
        'gmail.com', 'freemail.hu', 'citromail.hu', 'upcmail.hu', 'fibermail.hu',
        'gmx.de', 'googlemail.com', 'hotmail.com', 'icloud.com', 'indamail.hu',
        'invitel.hu', 'outlook.com', 'outlook.hu', 't-email.hu', 't-online.hu', 'yahoo.com'
    ];

    const similarityScore = (a, b) => {
        if (a === b) return 100;
        const longer = a.length > b.length ? a : b;
        const shorter = a.length > b.length ? b : a;
        let matches = 0;
        for (let i = 0; i < shorter.length; i++) {
            if (longer.includes(shorter[i])) matches++;
        }
        return (matches * 2 / (a.length + b.length)) * 100;
    };

    const checkEmailFrontend = (emailVal) => {
        if (!emailVal || !emailVal.includes('@')) return;
        const parts = emailVal.split('@');
        const domain = parts[parts.length - 1].toLowerCase();
        const local = parts.slice(0, -1).join('@');

        // 1. Ismert typo szótár
        if (EMAIL_TYPO_MAP[domain]) {
            const suggestion = `${local}@${EMAIL_TYPO_MAP[domain]}`;
            setEmailIsError(<p className="margin-0 color-error-500 text-md-regular">Biztosan jól írtad? Nem erre gondoltál? <span className="pointer" onClick={() => emailInputChange(suggestion)}>{suggestion}</span></p>);
            return;
        }

        // 2. Hasonlóság alapú suggestion ismert domain-ekhez
        let best = { word: domain, score: 0 };
        for (const known of EMAIL_KNOWN_DOMAINS) {
            const score = similarityScore(known, domain);
            if (score > best.score) best = { word: known, score };
        }
        if (best.score > 60 && best.word !== domain) {
            const suggestion = `${local}@${best.word}`;
            setEmailIsError(<p className="margin-0 color-error-500 text-md-regular">Biztosan jól írtad? Nem erre gondoltál? <span className="pointer" onClick={() => emailInputChange(suggestion)}>{suggestion}</span></p>);
            return;
        }

        // 3. Nincs probléma
        setEmailIsError("");
    };

    const handleEmailBlur = () => {
        checkEmailFrontend(email);
    }

    const handleEstimatedParticipants = (val) => {
        if (val !== "" && isNaN(val)) return;
        setEstimatedParticipantsError("");
        setEstimatedParticipantsWarning("");
        setEstimatedParticipants(val);
    }

    const hasValidParticipantsValue = (value) => {
        if (value === "" || value === null || value === undefined) return false;
        return !Number.isNaN(Number(value));
    }

    const getParticipantsCapacityWarning = (value) => {
        if (!hasValidParticipantsValue(value)) return "";

        const participantCount = Number(value);
        if (provider.min_capacity && participantCount < +provider.min_capacity) {
            return "Figyelem! A helyszín minimális befogadóképessége több mint a tervezett létszám!";
        }

        if (provider.max_capacity && participantCount > +provider.max_capacity) {
            return "Figyelem! A helyszín befogadóképessége kisebb mint a tervezett létszám!";
        }

        return "";
    }

    const handleEstimatedParticipantsBlur = () => {
        if (!hasValidParticipantsValue(estimatedParticipants)) {
            setEstimatedParticipantsError("Szám megadása kötelező!");
            setEstimatedParticipantsWarning("");
            return;
        }

        setEstimatedParticipantsError("");
        setEstimatedParticipantsWarning(getParticipantsCapacityWarning(estimatedParticipants));
    }

    useEffect(() => {
        if (dateError) setDateError(false);
    }, [date])

    const submitHandler = (e) => {
        e.preventDefault();

        if (submitLockRef.current || isPreparingSubmit || isSendingPublicForm || isSendingAuthenticatedForm) {
            return;
        }

        submitLockRef.current = true;
        setIsPreparingSubmit(true);

        let hasError = false;
        const isGuest = status === "unauthenticated";
        const isPhoneRequired = !!provider.required_offerphone && !(status === "authenticated" && userData?.phone && userData.phone.toString().trim());

        if (isGuest && !name.trim()) {
            setNameIsError(true);
            hasError = true;
        } else {
            setNameIsError(false);
        }

        const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (isGuest && !email.trim()) {
            setEmailIsError("Kötelező mező!");
            hasError = true;
        } else if (isGuest && !EMAIL_REGEX.test(email.trim())) {
            setEmailIsError("Érvénytelen e-mail cím formátum!");
            hasError = true;
        } else if (isGuest) {
            setEmailIsError("");
        }

        if (isPhoneRequired && !phone.trim()) {
            setPhoneError(true);
            hasError = true;
        } else {
            setPhoneError(false);
        }

        if (!note.trim()) {
            setNoteError(true);
            hasError = true;
        } else {
            setNoteError(false);
        }

        if (!date || date.toString() === "Invalid Date") {
            setDateError(true);
            hasError = true;
        } else {
            setDateError(false);
        }

        if (!hasValidParticipantsValue(estimatedParticipants)) {
            setEstimatedParticipantsError("Kötelező mező!");
            setEstimatedParticipantsWarning("");
            hasError = true;
        } else {
            setEstimatedParticipantsError("");
            setEstimatedParticipantsWarning(getParticipantsCapacityWarning(estimatedParticipants));
        }

        if (!aszf) {
            setAszfError(true);
            hasError = true;
        } else {
            setAszfError(false);
        }

        if (hasError) {
            releaseSubmitLock();
            return;
        }

        let to_send = {
            date: createDateTimeString(date),
            max_participants: estimatedParticipants,
            message: note,
            phone: phone
        }
        
        if(status === "unauthenticated") {
            to_send["name"] = name;
            to_send["email"] = email;
        }

        if(userData.id !== 0 && weddingId == 0) {
            to_send["name"] = userData.name;
            to_send["email"] = userData.email;
        }

        if (provider.type?.id && provider.type.id.toString() !== PROV_TYPE__HELYSZIN) {
            to_send["city"] = city;
            to_send["wedding_provider_id"] = providerId;
            to_send["wedding_provider_name"] = providerName;
        }

        if(!executeRecaptcha) {
            console.error("not available to execute recaptcha")
            releaseSubmitLock();
            return;
        }

        if (status === "authenticated" && userData.id !== 0 && weddingId !== 0) {
            // reCAPTCHA silent mode - bejelentkezett felhasználóknál is futtatjuk
            getRecaptchaToken(executeRecaptcha, "offer").then((gRecaptchaToken) => {
                to_send["captcha_response"] = gRecaptchaToken;
                sendAuthenticatedForm({ wedding_id: weddingId, params: to_send, provider_id: provider.id });
            }).catch(() => {
                releaseSubmitLock();
            });
        } else {
            getRecaptchaToken(executeRecaptcha, "offer").then((gRecaptchaToken) => {
                to_send["captcha_response"] = gRecaptchaToken;
                sendForm({ params: to_send, provider_id: provider.id });
            }).catch(() => {
                releaseSubmitLock();
            });
        }

    }

    const successClose = () => { setSuccessModal(false); };
    const isSubmittingOffer = isPreparingSubmit || isSendingPublicForm || isSendingAuthenticatedForm;
    
    const successConfirm = () => {
        successClose();
        setShowLogin(true);
        setShowLoginState("login");
        setRedirectURL(`${window.location.pathname}`);
    };

    const buttons = [
        <CustomButton key={"success_button_1"} className="w-75" size="lg" hierarchy="primary" onClick={successConfirm} >Bejelentkezés</CustomButton>,
        <CustomButton key={"success_button_2"} className="w-25" size="lg" hierarchy="secondary-gray" onClick={successClose} >Bezárás</CustomButton>
    ];

    const formContent = (
        <>
            <p className={`margin-0 color-gray-800 text-xl-medium ${classes["public-provider-form-title"]}`}>Kapcsolatfelvétel</p>
            {provider.contact?.name && <div className={classes["public-provider-form-recipient"]}>
                {provider.contact.image && <Avatar size={"sm"} profile url={provider.contact.image} />}
                <div className="d-flex flex-column min-width-0">
                    <p className="margin-0 color-gray-500 text-xs-regular">Kapcsolattartó:</p>
                    <p className="margin-0 color-gray-800 text-sm-semibold text-truncate">{provider.contact.name}</p>
                </div>
            </div>}
            <form className={`${classes["public-provider-section-container"]}`} onSubmit={e => submitHandler(e)}>
                {lastMessage && lastMessage.message && <LastMessageSection message={lastMessage.message} ts={lastMessage.ts} />}
                {status === "unauthenticated" && <>
                    <TextInput className={`w-100`} value={name} onChange={e => nameInputChange(e.target.value)} type={"text"} hint={nameIsError ? "Kötelező mező" : undefined} error={nameIsError} label={"Ön neve"} required_mark />
                    <TextInput className={`w-100`} value={email} onChange={e => emailInputChange(e.target.value)} onBlur={handleEmailBlur} type={"text"} hint={emailIsError} error={emailIsError} label={"E-mail cím"} required_mark />
                </>}
                <TextInput
                    className={`w-100`}
                    value={phone}
                    onChange={e => {
                        setPhone(e.target.value);
                        setPhoneError(false);
                    }}
                    hint={phoneError ? "Kötelező mező!" : undefined}
                    error={phoneError}
                    label={"Telefonszám"}
                    required_mark={!!provider.required_offerphone && !(status === "authenticated" && userData?.phone && userData.phone.toString().trim())}
                />
                <div className="d-flex flex-column gap-2 w-100">
                    <div className="d-flex flex-row text-nowrap gap-1 color-gray-700 text-sm-medium">
                        Üzenet
                        <div className="color-error-600">*</div>
                    </div>
                    <textarea
                        id="public-provider-form-text-area"
                        className={`${classes["public-provider-message-textarea"]} ${noteError ? classes["error"] : ""}`}
                        value={note}
                        onChange={(e) => {
                            setNote(e.target.value);
                            setNoteError(false);
                        }}
                        rows={5}
                    />
                    {noteError && <div className="color-error-500 text-sm-regular">Kötelező mező!</div>}
                </div>
                <div className={classes["public-provider-date-participants-row"]}>
                    <div className={`${classes["public-provider-date-field"]} d-flex flex-column align-self-stretch position-relative`}>
                        <CustomDatepicker date={date} setDate={(val) => setDate(val)} title={"Dátum"} noTime required_mark error={dateError} hint={dateError ? "Tervezett dátum – kötelező mező!" : undefined} />
                    </div>
                    <TextInput
                        className={`${classes["public-provider-participants-field"]} w-100`}
                        value={estimatedParticipants}
                        onChange={e => handleEstimatedParticipants(e.target.value)}
                        onBlur={handleEstimatedParticipantsBlur}
                        type={"text"}
                        hint={estimatedParticipantsError || estimatedParticipantsWarning}
                        error={!!estimatedParticipantsError}
                        label={"Tervezett létszám"}
                        placeholder={"Programra tervezett létszám"}
                        required_mark
                    />
                </div>
                {
                    provider.type?.id && provider.type.id.toString() !== PROV_TYPE__HELYSZIN &&
                    <>
                        <TextInput className={`w-100`} value={city} onChange={e => setCity(e.target.value)} label={"Város"} placeholder={"Város"} />
                        <ProviderDropdownPublic starting_values={{ favourites: [], default_providers: [] }} providerId={providerId} setProviderId={setProviderId} setProviderName={setProviderName} starting_name={providerName} has_switch={false} title={"Helyszín"} />
                    </>
                }
                <FormControl id="aszf" theme="primary" checked={aszf} onChange={() => {
                    const next = !aszf;
                    setAszf(next);
                    if (next) setAszfError(false);
                }} type="checkbox" size={"sm"}
                    main_text={<p className="margin-0 padding-0 color-gray-700 text-sm-medium">
                        Elfogadom az <Link href="/adatvedelmi_tajekoztato" title="aszf" rel="noopener noreferrer" target="_blank" className="margin-0 padding-0 color-gray-700 text-sm-medium">adatkezelési szabályzatot</Link>
                    </p>}
                />
                {aszfError && <div className="color-error-500 text-sm-regular">Kérlek, fogadd el az ÁSZF-et</div>}
                <div className={classes["public-provider-form-submit-bar"]}>
                    <div className="d-flex flex-row gap-2 w-100 align-items-center align-self-stretch">
                        <CustomButton className={(status === "authenticated" && userData.id !== 0 && weddingId !== 0) ? "w-100" : ""} size="lg" hierarchy="primary" icon="leading" is_svg={true} svg={Send} disabled={isSubmittingOffer} isLoading={isSubmittingOffer} loadingText="Küldés..." >Küldés</CustomButton>
                        {status === "authenticated" && userData.id !== 0 && weddingId !== 0 && provider.is_favourite && 
                            <CustomButton className={"w-100"} size="lg" hierarchy="tertiary-gray" icon="leading" is_svg={true} svg={MessageCircle} href={`/szervezom/${weddingId}/szolgaltatok?provider_id=${provider.id}`} >Üzenetek</CustomButton>
                        }
                    </div>
                </div>
            </form>
        </>
    );

    return <>
        <div
            className={`${classes["public-provider-form-main"]}`}
            data-provider-form-placement={isMobileViewport ? "inline" : "sidebar"}
        >
            <div
                ref={(element) => {
                    inlineFormRef.current = element;
                    if (!isMobileViewport || !contactMobile) formRef.current = element;
                }}
                className={`${classes["public-provider-form-main-inner"]}`}
            >
                {(!isMobileViewport || !contactMobile) && formContent}
            </div>
        </div>
        <Offcanvas
            show={isMobileViewport && contactMobile}
            onHide={() => setContactMobile(false)}
            placement="bottom"
            className={classes["mobile-offcanvas"]}
            backdropClassName={classes["mobile-offcanvas-backdrop"]}
            data-provider-form-placement="sheet"
        >
            <Offcanvas.Header className={classes["mobile-offcanvas-header"]}>
                <p className="margin-0 color-gray-900 text-md-medium flex-grow-1 text-truncate">Kapcsolatfelvétel</p>
                <CustomButton
                    size="sm"
                    hierarchy="tertiary-gray"
                    icon="only"
                    is_svg={true}
                    svg={X}
                    onClick={() => setContactMobile(false)}
                    title="Bezárás"
                />
            </Offcanvas.Header>
            <Offcanvas.Body className={classes["mobile-offcanvas-body"]}>
                <div ref={isMobileViewport && contactMobile ? formRef : null} className={classes["mobile-offcanvas-inner"]}>
                    {isMobileViewport && contactMobile && formContent}
                </div>
            </Offcanvas.Body>
        </Offcanvas>
        <CustomModal show={successModal} close={successClose} type={"success"} title={"Üzenetet sikeresen elküldtük"} buttons={buttons}>
            <div className="color-gray-500 text-sm-regular">Jelentkezz be, hogy követhesd az esküvőddel kapcsolatos levelezéseket</div>
        </CustomModal>
        
        {/* Email Verification Modal */}
        {showVerificationModal && <EmailVerificationModal
            isOpen={showVerificationModal}
            onClose={() => setShowVerificationModal(false)}
            email={email}
            score={verificationScore}
            onResend={null}
            isResending={false}
        />}
        
        {/* reCAPTCHA v2 Modal */}
        {showRecaptchaV2Modal && <RecaptchaV2Modal
            show={showRecaptchaV2Modal}
            onClose={handleV2Close}
            onSuccess={handleV2Success}
        />}
    </>
}

function LastMessageSection({ message, ts }) {
    const [isExpanded, setIsExpanded] = useState(false);
    const sanitizedMessage = useSanitizedHTMLStrict(message || '');
    const messageDate = ts ? new Date(ts) : null;
    const hasValidMessageDate = messageDate && !Number.isNaN(messageDate.getTime());

    return <div className={`${classes["form-last-message-container"]}`}>
        <div className={`d-flex flex-row align-items-center align-self-stretch justify-content-between ${classes["last-message-border-bottom"]}`}>
            <p className="margin-0 color-gray-700 text-xs-bold">Utoljára küldött üzenet</p>
            {hasValidMessageDate && <p className="margin-0 color-gray-500 text-xs-regular">{formatDate(messageDate)}</p>}
        </div>
        <div className={`${classes["last-message-description"]} ${isExpanded ? classes["expanded"] : ""} text-xs-regular`} dangerouslySetInnerHTML={{ __html: sanitizedMessage }} />
        {!isExpanded && <div className={`${classes["last-message-border-top"]} w-100`}>
            <CustomButton size="sm" hierarchy="link-gray" onClick={() => setIsExpanded(true)} >Teljes üzenet megtekintés</CustomButton>
        </div>}
    </div>
}